/**
 * objectifsConcis.js — Référentiel d'objectifs CONCIS et de critères PRÉCIS
 * à utiliser dans les fiches d'évaluation dynamique (Plateau + Circulation).
 *
 * Pourquoi ce fichier ?
 *   Les objectifs du livret REMC (`livretApprentissageA.js`) sont rédigés en
 *   phrases longues (« À l'issue de la séance, l'élève réalise un freinage
 *   progressif avant + arrière avec une répartition 70/30 et s'arrête en
 *   sécurité. »). Lors d'une évaluation dynamique, le moniteur a besoin
 *   d'objectifs **courts, formulés en verbe d'action**, et de **critères
 *   mesurables** (chiffres, repères) pour piloter la séance en temps réel.
 *
 * Convention :
 *   - `objectifCourt` : phrase d'action infinitive, ≤ 12 mots, sans
 *     préambule (« À l'issue… »), mesurable ou immédiatement observable.
 *   - `criteresPrecis` : 3 à 5 items, chacun ≤ 8 mots, **chiffrés ou
 *     observables sans ambiguïté** (distance, durée, comptage, ratio).
 *     Pas de « L'élève… ».
 *
 * Tous les codes du livret REMC catégorie A sont couverts (C1, C2, C3, C4).
 */

export const OBJECTIFS_CONCIS = {
  /* ═══ C1 — HORS CIRCULATION (Plateau) ════════════════════════════════ */
  'C1-a': {
    objectifCourt: 'Porter un EPI complet et conforme',
    criteresPrecis: [
      'Casque ECE 22-06 + jugulaire serrée (test index)',
      'Gants CE EN13594 portés à chaque montée',
      '≥ 3 EPI recommandés cités (blouson, dorsale, bottes)',
      'Casque inspecté avant chaque séance',
    ],
  },
  'C1-b': {
    objectifCourt: 'Identifier commandes, organes et voyants',
    criteresPrecis: [
      '6 commandes nommées sans aide',
      '4 organes essentiels cités (moteur, transmission, freins, suspensions)',
      '5 voyants tableau de bord interprétés',
      'Coupe-circuit distingué du contacteur',
    ],
  },
  'C1-c': {
    objectifCourt: 'Déplacer la moto à la poussette en sécurité',
    criteresPrecis: [
      'Pousser droit sur 10 m sans déséquilibre',
      'Demi-tour poussette ≤ 4 m de diamètre',
      'Marche arrière contrôlée sur 3 m',
      'Béquillage / débéquillage fluide',
    ],
  },
  'C1-d': {
    objectifCourt: 'Adopter une position de conduite ergonomique',
    criteresPrecis: [
      'Pieds sur repose-pieds, talons rentrés',
      'Genoux serrés au réservoir',
      'Bras fléchis, épaules relâchées',
      'Regard horizontal, dos droit',
      'Descente : frein avant maintenu',
    ],
  },
  'C1-e': {
    objectifCourt: 'Démarrer sans caler · arrêter à ≤ 1 m du repère',
    criteresPrecis: [
      '5 démarrages consécutifs sans calage',
      'Démarrage en pente 10 % maîtrisé',
      'Arrêt précis : ≤ 1 m du point',
      'Pied gauche stabilisé à l\'arrêt',
    ],
  },
  'C1-f': {
    objectifCourt: 'Monter et rétrograder sans à-coup ni calage',
    criteresPrecis: [
      'Passage 1→2→3 fluide en accélération',
      'Rétrogradation au bon régime (< 3500 tr/min)',
      'Aucun calage sur 5 cycles',
      'Pression sélecteur dosée (pas de claquement)',
    ],
  },
  'C1-g': {
    objectifCourt: 'Freiner avec répartition 70/30 sans blocage',
    criteresPrecis: [
      'Avant 70 % · arrière 30 %',
      '50 km/h → arrêt en ≤ 20 m (sec)',
      'Aucun blocage de roue',
      'Embrayage débrayé avant l\'arrêt',
    ],
  },
  'C1-h': {
    objectifCourt: 'Porter le regard loin et fluidifier la trajectoire',
    criteresPrecis: [
      'Regard 2 cônes / 50 m à l\'avance',
      'Tête tournée AVANT le virage',
      'Aucun blocage du regard sur l\'obstacle',
      'Trajectoire propre, pas de correction tardive',
    ],
  },
  'C1-i': {
    objectifCourt: 'Tenir l\'équilibre à allure lente sur parcours sinueux',
    criteresPrecis: [
      'Allure lente < 10 km/h sans poser le pied',
      '5 cônes franchis sans toucher',
      'Stabilité maintenue avec passager (si applicable)',
      'Pieds aux repose-pieds 100 % du temps',
    ],
  },
  'C1-j': {
    objectifCourt: 'Doser l\'embrayage en patinage contrôlé',
    criteresPrecis: [
      'Point de patinage trouvé en < 2 s',
      'Manœuvre lente sans calage',
      'Allure ralentie via embrayage seul',
      'Aucun claquage du levier',
    ],
  },
  'C1-k': {
    objectifCourt: 'Contre-braquer dès 30 km/h, trajectoire de sécurité',
    criteresPrecis: [
      'Contre-braquage net amorcé > 30 km/h',
      'Trajectoire extérieur → intérieur → extérieur',
      'Inclinaison adaptée, pas de redressement',
      'Point de corde respecté',
    ],
  },
  'C1-l': {
    objectifCourt: 'Freinage d\'urgence 50 km/h ≤ 14 m sans chute',
    criteresPrecis: [
      '50 km/h → arrêt ≤ 14 m (sec)',
      'Avant + arrière en simultané',
      'Aucun blocage de roue / aucune chute',
      'Embrayage débrayé en fin de freinage',
    ],
  },

  /* ═══ C2 — CIRCULATION NORMALE ═══════════════════════════════════════ */
  'C2-a': {
    objectifCourt: 'Respecter le Code de la route en toute situation',
    criteresPrecis: [
      'Stops + cédez-le-passage respectés à 100 %',
      'Vitesses limites respectées (± 5 km/h)',
      'Aucune infraction sur 30 min de conduite',
      'Marquage au sol respecté (lignes continues)',
    ],
  },
  'C2-b': {
    objectifCourt: 'Lire la signalisation et capter les indices utiles',
    criteresPrecis: [
      'Tous les panneaux verbalisés à voix haute',
      'Anticipation 100 m / 5 s minimum',
      'Indices captés : feux stop, clignotants, piétons',
      'Aucune surprise sur changement de signalisation',
    ],
  },
  'C2-c': {
    objectifCourt: 'Se positionner selon le contexte (hors agglo/agglo)',
    criteresPrecis: [
      'Hors agglo : 1/3 droit de la voie',
      'Agglo : centre ou 1/3 gauche',
      'Jamais de chevauchement de la ligne médiane',
      'Distance ≥ 1 m des véhicules stationnés',
      'Trajectoire de sécurité en virage (ext→int→ext)',
    ],
  },
  'C2-d': {
    objectifCourt: 'Adapter l\'allure à la situation et au gabarit',
    criteresPrecis: [
      'Vitesse adaptée AVANT le virage',
      'Aucune sur-vitesse en zone scolaire/agglo',
      'Allure réduite intersection / piétons',
      'Régime moteur cohérent (zone verte)',
    ],
  },
  'C2-e': {
    objectifCourt: 'Stationner et redémarrer en sécurité',
    criteresPrecis: [
      'Aire choisie : visible, stable, hors trafic',
      'Béquille latérale sur sol dur',
      'Vérif droite/gauche AVANT redémarrage',
      'Clignotant utilisé pour quitter le stationnement',
    ],
  },
  'C2-f': {
    objectifCourt: 'Tourner à droite / gauche avec clignotant et regard',
    criteresPrecis: [
      'Clignotant 3 s avant changement de direction',
      'Contrôle rétro + angle mort systématique',
      'Regard porté vers la sortie',
      'Trajectoire propre (pas de coupe d\'angle)',
    ],
  },
  'C2-g': {
    objectifCourt: 'Franchir les intersections en respectant les priorités',
    criteresPrecis: [
      'Stop : arrêt complet + pied au sol',
      'Cédez-le-passage : ralentissement effectif',
      'Priorité à droite identifiée',
      'Feu orange : décision claire (passe / s\'arrête)',
    ],
  },
  'C2-h': {
    objectifCourt: 'Négocier un rond-point avec clignotant et regard',
    criteresPrecis: [
      'Priorité aux usagers déjà engagés',
      'Clignotant droit après la sortie précédente',
      'Trajectoire fluide, pas de chevauchement',
      'Regard porté vers la sortie',
    ],
  },

  /* ═══ C3 — CONDITIONS DIFFICILES ═════════════════════════════════════ */
  'C3-a': {
    objectifCourt: 'Maintenir 2 secondes mini (4 s par pluie)',
    criteresPrecis: [
      'Règle « 21 - 22 » appliquée sur 100 % du trajet',
      'Doublée à 4 s sous la pluie / brouillard',
      'Distance latérale ≥ 1 m des autres usagers',
      'Aucune queue de poisson',
    ],
  },
  'C3-b': {
    objectifCourt: 'Croiser, suivre, dépasser en sécurité',
    criteresPrecis: [
      'Distance ≥ 1 m lors du croisement',
      'Dépassement : visibilité + temps suffisants',
      'Aucun dépassement en ligne continue',
      'Rétro + angle mort vérifiés AVANT et APRÈS',
    ],
  },
  'C3-c': {
    objectifCourt: 'Négocier les virages selon la trajectoire de sécurité',
    criteresPrecis: [
      'Freinage TERMINÉ avant l\'entrée du virage',
      'Trajectoire ext→int→ext respectée',
      'Inclinaison adaptée au rayon',
      'Regard porté vers la sortie',
    ],
  },
  'C3-d': {
    objectifCourt: 'Communiquer son intention aux autres usagers',
    criteresPrecis: [
      'Clignotant ≥ 3 s avant manœuvre',
      'Clignotant éteint après manœuvre',
      'Feux de détresse en cas de ralentissement brutal',
      'Klaxon utilisé à bon escient (alerte ≠ agression)',
    ],
  },
  'C3-e': {
    objectifCourt: 'Cohabiter avec les usagers vulnérables',
    criteresPrecis: [
      'Distance ≥ 1,5 m d\'un cycliste / piéton',
      'Allure réduite à proximité d\'enfants',
      'Anticipation des trajectoires erratiques',
      'Aucun comportement intimidant',
    ],
  },
  'C3-f': {
    objectifCourt: 'Cohabiter avec les poids lourds en sécurité',
    criteresPrecis: [
      'Jamais dans l\'angle mort PL (arrière-droite)',
      'Distance ≥ 50 m derrière un PL',
      'Dépassement franc, sans hésitation',
      'Visibilité conducteur PL vérifiée (rétro)',
    ],
  },
  'C3-g': {
    objectifCourt: 'S\'insérer et sortir d\'une voie rapide',
    criteresPrecis: [
      'Vitesse adaptée AVANT insertion (≥ 90 km/h)',
      'Bande d\'insertion utilisée à 100 %',
      'Clignotant + contrôle rétro angle mort',
      'Sortie : déboitement anticipé 200 m avant',
    ],
  },
  'C3-h': {
    objectifCourt: 'Circuler en file dense sans inter-files',
    criteresPrecis: [
      'Distance ≥ 1 s du véhicule devant',
      'Aucun zigzag inutile',
      'Anticipation feux / freinages',
      'Position adaptée : centre/1/3 gauche en agglo',
    ],
  },
  'C3-i': {
    objectifCourt: 'Circuler inter-files dans la zone CIF autorisée',
    criteresPrecis: [
      'Zone autorisée respectée (axes ≥ 4 voies)',
      'Vitesse ≤ 50 km/h en CIF',
      '+ 20 km/h max vs trafic adjacent',
      'Aucun CIF en voie unique ou virage',
    ],
  },
  'C3-j': {
    objectifCourt: 'Adapter sa conduite pluie / nuit / brouillard',
    criteresPrecis: [
      'Vitesse réduite de 20 % minimum',
      'Distance de sécurité doublée',
      'Feux de croisement allumés',
      'Visière relevée + désembuage actif',
    ],
  },
  'C3-k': {
    objectifCourt: 'Franchir tunnels et ponts en sécurité',
    criteresPrecis: [
      'Feux de croisement allumés dans le tunnel',
      'Visière relevée à l\'entrée (adaptation lumière)',
      'Distance de sécurité doublée (vent latéral)',
      'Vitesse adaptée à la signalisation',
    ],
  },

  /* ═══ C4 — AUTONOMIE ════════════════════════════════════════════════ */
  'C4-a': {
    objectifCourt: 'Suivre un itinéraire en autonomie',
    criteresPrecis: [
      'Préparation : carte / GPS consultés AVANT',
      'Itinéraire respecté sans aide moniteur',
      'Aucune erreur de direction majeure',
      'Adaptation aux imprévus (déviation)',
    ],
  },
  'C4-b': {
    objectifCourt: 'Gérer un trajet longue distance',
    criteresPrecis: [
      'Pause toutes les 2 h ou 200 km',
      'Hydratation + collation régulières',
      'Vigilance maintenue (pas de micro-sommeil)',
      'Préparation moto : pneus, niveaux, EPI',
    ],
  },
  'C4-c': {
    objectifCourt: 'Identifier les facteurs de risque humains',
    criteresPrecis: [
      'Alcool : 0 g/L au guidon',
      'Médicaments incompatibles cités',
      'Téléphone : 0 utilisation en roulant',
      'Stress / fatigue identifiés et gérés',
    ],
  },
  'C4-d': {
    objectifCourt: 'Adopter la bonne conduite en cas d\'accident',
    criteresPrecis: [
      'PAS : Protéger / Alerter / Secourir',
      'Numéros : 112 / 15 / 17 / 18 connus',
      'Constat amiable rempli correctement',
      'Aucun déplacement de blessé sans nécessité',
    ],
  },
  'C4-e': {
    objectifCourt: 'Utiliser les aides à la conduite (ABS, TC)',
    criteresPrecis: [
      'Fonctionnement ABS expliqué',
      'Voyant ABS allumé = anomalie détectée',
      'Limites des aides connues (pluie, gravillons)',
      'TC / mode pluie utilisé si équipé',
    ],
  },
  'C4-f': {
    objectifCourt: 'Entretenir sa moto pour rouler en sécurité',
    criteresPrecis: [
      'Pression pneus vérifiée à froid',
      'Niveaux (huile, frein, refroidissement) contrôlés',
      'Chaîne tendue + lubrifiée (cf. RTM)',
      'Carnet d\'entretien à jour',
    ],
  },
  'C4-g': {
    objectifCourt: 'Adopter une éco-conduite raisonnée',
    criteresPrecis: [
      'Régime moteur en zone verte (1500-4000 tr/min)',
      'Anticipation = moins de freinages',
      'Coupure moteur si arrêt > 30 s',
      'Charge utile maîtrisée (bagages serrés)',
    ],
  },
};

/**
 * Renvoie le bloc concis pour un code REMC, ou null si pas couvert.
 */
export function getObjectifConcis(code) {
  return OBJECTIFS_CONCIS[code] || null;
}

/**
 * Helper : renvoie l'objectif court formaté « Savoir … » pour adoption rapide.
 */
export function getObjectifSavoirCourt(code) {
  const o = OBJECTIFS_CONCIS[code];
  if (!o) return null;
  // Commence par un verbe d'action → « Savoir » + verbe
  return `Savoir ${o.objectifCourt.charAt(0).toLowerCase()}${o.objectifCourt.slice(1)}`;
}
